(function () {
  'use strict';
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  function specifications(html, locale = 'en', Parser = DOMParser) {
    const doc = new Parser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('script,style,iframe,object,template').forEach(node => node.remove());
    const result = new Map();
    const duplicates = new Set();
    const wanted = locale.split('-')[0] === 'es' ? 'especificaciones' : 'specifications';
    let active = false;
    function add(label, value) {
      label = clean(label).replace(/:\s*$/, '');
      value = clean(value);
      const key = label.toLowerCase();
      if (!key || !value || duplicates.has(key)) return;
      if (result.has(key)) { result.delete(key); duplicates.add(key); return; }
      result.set(key, { label, value });
    }
    // Only explicitly labelled specifications; never infer facts from prose.
    for (const node of doc.querySelectorAll('h1,h2,h3,h4,h5,h6,tr,li')) {
      if (/^H[1-6]$/.test(node.tagName)) {
        active = clean(node.textContent).toLowerCase().replace(/:$/, '') === wanted;
      } else if (active && node.tagName === 'TR') {
        const cells = [...node.children].filter(n => /^(TH|TD)$/.test(n.tagName));
        if (cells.length === 2) add(cells[0].textContent, cells[1].textContent);
      } else if (active && node.tagName === 'LI' && !node.querySelector('li')) {
        const text = clean(node.textContent);
        const colon = text.indexOf(':');
        if (colon > 0) add(text.slice(0, colon), text.slice(colon + 1));
      }
    }
    return result;
  }
  function commonRows(left, right) {
    return [...left].filter(([key]) => right.has(key)).map(([key, entry]) => [entry.label, entry.value, right.get(key).value]);
  }
  function appendRows(body, rows) {
    for (const values of rows) {
      const row = body.ownerDocument.createElement('tr');
      values.forEach((value, i) => {
        const cell = body.ownerDocument.createElement(i === 0 ? 'th' : 'td');
        if (i === 0) cell.setAttribute('scope', 'row');
        cell.textContent = value || '—';
        row.append(cell);
      });
      body.append(row);
    }
  }
  async function addToCart(form, root) {
    const button = form.querySelector('button');
    if (button.disabled) return;
    const status = root.querySelector('[data-compare-status]');
    button.disabled = true;
    status.textContent = '';
    try {
      const response = await fetch(`${root.dataset.root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: form.querySelector('[name="id"]').value, quantity: 1 }] })
      });
      if (!response.ok) throw Error('cart');
      status.textContent = root.dataset.added;
    } catch (_) { status.textContent = root.dataset.error; }
    finally { button.disabled = false; }
  }
  function normalizeRoot(value) {
    const path = String(value || '/');
    if (!/^\/(?:[a-zA-Z0-9-]+\/?)*$/.test(path)) throw Error('root');
    return path.replace(/\/+$/, '') + '/';
  }
  function selectCandidate(current, products) {
    if (!current || typeof current.type !== 'string' || !current.type.trim() || !Number.isFinite(current.price) || !Array.isArray(products)) return null;
    return products.find(p => p && String(p.id) !== String(current.id) && p.handle !== current.handle && p.available === true && p.type === current.type && Number.isFinite(p.price) && p.price >= current.price * 0.5 && p.price <= current.price * 2) || null;
  }
  function realOptions(product) {
    return (product.options || []).filter(o => Array.isArray(o.values) && o.values.length && !(o.name === 'Title' && o.values.length === 1 && o.values[0] === 'Default Title'));
  }
  function buildTable(root, products, Parser) {
    const d = root.dataset;
    const make = (tag, text, attrs = {}) => {
      const node = root.ownerDocument.createElement(tag);
      if (text !== undefined) node.textContent = text;
      Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
      return node;
    };
    const money = new Intl.NumberFormat(d.locale || 'en', { style: 'currency', currency: d.currency || 'USD' });
    const price = cents => money.format(cents / 100);
    const table = make('table');
    table.append(make('caption', d.heading));
    const cols = make('colgroup');
    cols.append(make('col', undefined, {class:'sth-compare-label'}), make('col'), make('col'));
    table.append(cols);
    const head = make('thead'), headings = make('tr');
    headings.append(make('th', d.featureLabel, {scope:'col'}));
    products.forEach(p => {
      const cell = make('th', undefined, {scope:'col'});
      // Build local links from validated handles; recommendation URLs are untrusted.
      cell.append(make('a', p.title, {href:d.root + 'products/' + p.handle}));
      headings.append(cell);
    });
    head.append(headings); table.append(head);
    const body = make('tbody');
    appendRows(body, [[d.priceLabel, ...products.map(p => price(p.price) + (p.price_varies ? '+' : ''))]]);
    const options = products.map(realOptions);
    if (options.every(o => o.length)) appendRows(body, [[d.optionsLabel, ...options.map(list => list.map(o => o.name + ': ' + o.values.length).join('; '))]]);
    const parsed = products.map(p => specifications(p.description, d.locale, Parser));
    appendRows(body, commonRows(...parsed)); table.append(body);
    const foot = make('tfoot'), row = make('tr');
    row.append(make('th', d.variantLabel, {scope:'row'}));
    products.forEach((p, index) => {
      const cell = make('td');
      const variant = (p.variants || []).find(v => v.available && /^\d+$/.test(String(v.id)) && Number.isFinite(v.price));
      if (variant && p.available) {
        if (options[index].length) cell.append(make('p', variant.title));
        cell.append(make('p', price(variant.price)));
        const form = make('form', undefined, {method:'post',action:d.root + 'cart/add'});
        form.append(make('input', undefined, {type:'hidden',name:'id',value:variant.id}), make('input', undefined, {type:'hidden',name:'quantity',value:'1'}));
        form.append(make('button', d.addLabel, {type:'submit','aria-label':d.addLabel + ': ' + p.title}));
        form.addEventListener('submit', event => { event.preventDefault(); addToCart(form, root); });
        cell.append(form);
      } else cell.textContent = d.unavailableLabel;
      row.append(cell);
    });
    foot.append(row); table.append(foot);
    const fragment = root.ownerDocument.createDocumentFragment();
    fragment.append(table, make('p', '', {role:'status','aria-live':'polite','data-compare-status':''}), make('a', d.cartLabel, {href:d.root + 'cart'}));
    return fragment;
  }
  async function initSection(root, fetcher = fetch, Parser = DOMParser) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    root.hidden = true;
    try {
      const d = root.dataset;
      d.root = normalizeRoot(d.root);
      const current = {id:d.productId,handle:d.current,type:d.type,price:Number(d.price)};
      if (!/^\d+$/.test(current.id) || !current.type?.trim() || !/^[a-z0-9-]+$/.test(current.handle)) return;
      const get = async url => {
        const response = await fetcher(url, {headers:{Accept:'application/json'}});
        if (!response.ok) throw Error('product');
        return response.json();
      };
      const recommendations = await get(`${d.root}recommendations/products.json?product_id=${current.id}&limit=10&intent=related`);
      const candidate = selectCandidate(current, recommendations.products);
      if (!candidate || !/^[a-z0-9-]+$/.test(candidate.handle)) return;
      const products = await Promise.all([current, candidate].map(p => get(`${d.root}products/${p.handle}.js`)));
      if (!products.every((p,i) => p && String(p.id) === String([current,candidate][i].id) && p.handle === [current,candidate][i].handle) || products[0].type !== current.type || !selectCandidate(products[0], [products[1]])) return;
      root.replaceChildren(buildTable(root, products, Parser));
      root.hidden = false;
    } catch (_) { /* No partial or empty visible comparison on request/data errors. */ }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { specifications, commonRows, appendRows, addToCart, selectCandidate, normalizeRoot, initSection };
  if (typeof document === 'undefined' || window.sthCompareInitialized) return;
  window.sthCompareInitialized = true;
  const init = () => document.querySelectorAll('[data-sth-compare]').forEach(root => initSection(root));
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
